import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import * as icons from '@fortawesome/free-solid-svg-icons';
import {
  CdkDragDrop,
  moveItemInArray,
  copyArrayItem,
} from '@angular/cdk/drag-drop';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { AuthService } from 'src/app/shared/services/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BoardUserData } from '../up-board-editor/up-board-editor.component';
import {
  getUsersInState,
  getUsersNotInState,
  normalizeEmployee,
  denormalizeForWrite,
  isValidStateForChannel,
  getAvailableForChannel,
  calculateUpAndOnDeck,
  normalizeEmployees,
  isEmployeeUnavailable,
  isEmployeeOnBreak,
  isEmployeeBusyInAnyChannel,
  isEmployeeInactive,
  isEmployeeGloballyUnavailable,
} from 'src/app/utils/board-user-utils';
import {
  NormalizedEmployee,
  RawEmployee,
  ChannelType,
} from 'src/app/utils/employee.model';
import {
  ColumnType,
  EmployeeAction,
} from '../employee-action-modal/employee-action-modal.component';

/**
 * Interface for tracking employee state changes for animations (Story 3.6)
 */
interface EmployeeChange {
  email: string;
  type: 'state_change' | 'column_entry' | 'column_exit' | 'position_change';
  fromColumn?: string;
  toColumn?: string;
  fromState?: string;
  toState?: string;
}
import { TransactionBoardService } from 'src/app/shared/services/transaction-board.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { getUserFriendlyMessage } from 'src/app/shared/services/transaction-error-messages';
import { TutorialService } from 'src/app/shared/services/tutorial.service';
import { TutorialModalService } from 'src/app/shared/services/tutorial-modal.service';
import { TutorialStateService } from 'src/app/shared/services/tutorial-state.service';
import { BoardPermissionsService } from 'src/app/shared/services/board-permissions.service';
declare var $: any;

@Component({
  selector: 'app-up-board',
  templateUrl: './up-board.component.html',
  styleUrls: ['./up-board.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * UpBoardComponent - Multi-Channel Board Display with Real-Time Updates
 * 
 * This component implements a flexible 2-4 column layout for managing employee queues
 * across multiple channels (instore, phone, online). It supports:
 * 
 * **Core Features:**
 * - Multi-column dynamic layout that responds to board config flags (enablePhoneChannel, enableOnlineChannel)
 * - Real-time employee list updates with <500ms latency via RxJS subscriptions
 * - Per-channel employee filtering and ordering (Floor Ups, With Guest, Phone Ups, Internet Ups)
 * - Drag-and-drop queue reordering within each column (requires admin authorization)
 * - Employee state transitions with validation (available → with_apt → delivery, etc.)
 * - Legacy format auto-upgrade pattern for backward compatibility (Story 1.6)
 * 
 * **Data Architecture:**
 * - Normalized multi-channel data model (NormalizedEmployee with channels[instore/phone/online])
 * - Automatic conversion from legacy format (flat state/order fields) to normalized
 * - Per-channel state and order values for independent queue management
 * - Cross-channel busy detection for phone/online availability
 * 
 * **Performance Optimizations:**
 * - ChangeDetectionStrategy.OnPush for minimal change detection cycles
 * - Manual markForCheck() calls after state updates
 * - RxJS takeUntil() pattern for subscription cleanup (no memory leaks)
 * - Efficient filtering with sort operations O(n log n) per update
 * 
 * **Column Definitions:**
 * - **Floor Ups**: Available, with_apt, with_apt_delivery, break, inactive (instore only)
 * - **With Guest**: ONLY with_guest, with_guest_delivery (instore only, always visible)
 * - **Phone Ups**: Employees eligible for phone channel (visible when enablePhoneChannel=true)
 * - **Internet Ups**: Employees eligible for online channel (visible when enableOnlineChannel=true)
 * 
 * **Integration Points:**
 * - AngularFirestore subscriptions for real-time board config and user data
 * - AuthTokenService for admin code validation (drag-drop authorization)
 * - UpBoardEmployeeComponent for individual employee card display
 * - TransactionBoardService for employee state transitions
 * 
 * @see Story 3.1 - Implement Multi-Column Board Layout (2-4 Columns)
 * @see Story 1.6 - Legacy format normalization and auto-upgrade pattern
 */
export class UpBoardComponent implements OnInit, OnDestroy {
   boardId: string | null;
  @Input() editMode: boolean;

  icons = icons;
  // board: Board | null | undefined;
  active: Employee[] | undefined;
  inactive: Employee[] | undefined;
  up: Employee | undefined;
  onDeck: Employee | undefined;
  modal_emp: Employee | undefined;
  modal_error: string | undefined;
  drag_event: CdkDragDrop<Employee[] | undefined> | undefined;
  old_active: Employee[] | null = null;
  nextInactiveEmployee: Employee | undefined;
  users: BoardUserData[] = [];
  
  /** Normalized employees for multi-channel support (Story 1.6) */
  normalizedUsers: NormalizedEmployee[] = [];
  
  /** Current channel for operations - defaults to instore for backward compatibility */
  currentChannel: ChannelType = 'instore';

  /** Whether to show delivery features for this board (defaults to true) */
  showDelivery: boolean = true;

  /** Track which channel is currently being dragged (for multi-channel support) */
  currentDragChannel: ChannelType = 'instore';


  // =============================================
  // Multi-Channel Support (Story 3.1)
  // =============================================
  
  /** Board configuration with feature flags for channels */
  boardConfig: any = {};
  
  /** Array of enabled channels based on board config */
  enabledChannels: ChannelType[] = ['instore'];

   // Per-channel employee lists (computed from users array)
   floorUpsEmployees: NormalizedEmployee[] = [];           // Floor Ups column (instore)
   withGuestEmployees: NormalizedEmployee[] = [];          // With Guest column (instore)
   phoneUpsEmployees: NormalizedEmployee[] = [];           // Phone Ups column
   internetUpsEmployees: NormalizedEmployee[] = [];        // Internet Ups column
   inactiveEmployees: NormalizedEmployee[] = [];           // Inactive employees section (Story 3.5.1)
   
   // Story 3.5.4: Context menu tracking for inactive employees
   activeContextMenuEmail: string | null = null;

   // Per-channel UP/OnDeck tracking (Story 3.6 - Badge Visibility Fix)
   // Each channel has its own UP and On Deck employees
   upInstore: NormalizedEmployee | undefined;              // UP for Floor Ups/With Guest columns
   onDeckInstore: NormalizedEmployee | undefined;          // On Deck for Floor Ups/With Guest columns
   upPhone: NormalizedEmployee | undefined;                // UP for Phone Ups column
   onDeckPhone: NormalizedEmployee | undefined;            // On Deck for Phone Ups column
   upOnline: NormalizedEmployee | undefined;               // UP for Internet Ups column
   onDeckOnline: NormalizedEmployee | undefined;           // On Deck for Internet Ups column

   // =============================================
   // Employee Action Modal State (Story 3.5)
   // =============================================
   
   /** Currently selected employee for action modal */
   selectedEmployee: NormalizedEmployee | null = null;
   
   /** Column where the selected employee was clicked */
   selectedColumn: ColumnType = 'instore';
   
   /** Whether the action modal is visible */
   showActionModal: boolean = false;

   // =============================================
   // Real-Time Sync Animation Support (Story 3.6)
   // =============================================
  
  /** Subject for subscription cleanup on component destroy */
  private destroy$ = new Subject<void>();
  
  /** Track animation states per employee (email -> CSS class) */
  employeeAnimations: Map<string, string> = new Map();
  
  /** Track previous UP employee for badge animation */
  previousUpEmail: string | null = null;
  
  /** Track previous On Deck employee for badge animation */
  previousOnDeckEmail: string | null = null;

  /** Flag to ensure initial tutorial is only triggered once per session */
  private initialTutorialTriggered: boolean = false;

  // =============================================
  // Legacy Mode Support (Story 3.9)
  // =============================================

  /**
   * Determines if the board should display in legacy 5-column table mode.
   * 
   * Rules:
   * - Multi-channel boards (phone OR online enabled, OR instore disabled) MUST use modern UI → return false
   * - Single-channel boards with useModernLayout=true → return false (opt-in to modern)
   * - Single-channel boards with useModernLayout=false/undefined → return true (legacy default)
   * 
   * This getter is reactive - updates automatically when boardConfig changes.
   */
  get isLegacyMode(): boolean {
    // A channel is only "active" if it's both purchased AND enabled
    // This prevents stale enablePhoneChannel/enableOnlineChannel values from forcing modern UI
    const phoneActive = this.boardConfig?.phoneChannelPurchased === true && 
                        this.boardConfig?.enablePhoneChannel === true;
    const onlineActive = this.boardConfig?.internetChannelPurchased === true && 
                         this.boardConfig?.enableOnlineChannel === true;
    
    // Multi-channel boards MUST use modern UI
    // Also force modern UI if instore is disabled (can't use legacy without instore)
    if (phoneActive || onlineActive || this.boardConfig?.enableInstoreChannel === false) {
      console.log('[DEBUG isLegacyMode] Multi-channel or instore disabled, returning false. phoneActive=', phoneActive, 'onlineActive=', onlineActive);
      return false;
    }
    // Single-channel boards: use legacy unless explicitly opted into modern UI
    const result = this.boardConfig?.useModernLayout !== true;
    console.log('[DEBUG isLegacyMode] Single-channel: useModernLayout=', this.boardConfig?.useModernLayout, '→ isLegacyMode=', result);
    return result;
  }

  /**
   * Gets the legacy state for an employee, handling both old and new data formats.
   * Multi-channel format: reads from channels.instore.state
   * Legacy format: reads from top-level state field
   * 
   * @param emp Employee object (normalized or legacy format)
   * @returns State string or 'inactive' if no state found
   */
  getLegacyState(emp: NormalizedEmployee): string {
    // Multi-channel format: channels.instore.state
    if (emp.channels?.instore?.state) {
      return emp.channels.instore.state;
    }
    // Legacy format: top-level state field (should not happen with normalized data, but safe fallback)
    return (emp as any).state || 'inactive';
  }

  /**
   * Gets active employees for legacy layout (not inactive state).
   * Sorted by order for proper queue display.
   */
  get legacyActiveEmployees(): NormalizedEmployee[] {
    return this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) !== 'inactive')
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0));
  }

  /**
   * Gets inactive employees for legacy layout.
   * Sorted alphabetically by name.
   */
  get legacyInactiveEmployees(): NormalizedEmployee[] {
    return this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) === 'inactive')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  /**
   * Gets the "UP" employee for legacy layout (first available in queue).
   */
  get legacyUp(): NormalizedEmployee | undefined {
    const available = this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) === 'available')
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0));
    return available[0];
  }

  /**
   * Gets the "On Deck" employee for legacy layout (second available in queue).
   */
  get legacyOnDeck(): NormalizedEmployee | undefined {
    const available = this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) === 'available')
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0));
    return available[1];
  }

  /**
   * Handles legacy drag-drop reordering.
   * Delegates to the main drop handler which already supports legacy behavior.
   */
  dropLegacy(event: CdkDragDrop<NormalizedEmployee[]>): void {
    // Reuse existing drop logic with instore channel
    this.drop(event, 'instore');
  }

  constructor(
    public db: AngularFirestore,
    public authTokenSvc: AuthTokenService,
    public authService: AuthService,
    public auth: AngularFireAuth,
    private route: ActivatedRoute,
    private router: Router,
    private transactionBoardSvc: TransactionBoardService,
    private notificationSvc: NotificationService,
    private tutorialService: TutorialService,
    private tutorialModalService: TutorialModalService,
    private tutorialStateService: TutorialStateService,
    private boardPermissionsService: BoardPermissionsService,
    private cdr: ChangeDetectorRef
  ) {
    this.boardId = null;
    this.editMode = false;
  }

  /**
   * Handles drag-drop events for both single-column (legacy) and multi-channel layouts.
   * If channel is provided, handles multi-channel reordering; otherwise uses legacy single-column.
   * 
   * @param event Drag-drop event from CDK
   * @param channel Optional channel for multi-channel reordering
   */
  drop(event: any, channel?: ChannelType) {
    // Multi-channel drag-drop (Story 3.1)
    if (channel) {
      // Store the channel being dragged for use in authorizeReorder
      this.currentDragChannel = channel;
      
      // Get the appropriate employee list for this channel
      let employeeList: NormalizedEmployee[] = [];
      switch (channel) {
        case 'instore':
          employeeList = this.floorUpsEmployees; // For Floor Ups (with_guest excluded)
          break;
        case 'phone':
          employeeList = this.phoneUpsEmployees;
          break;
        case 'online':
          employeeList = this.internetUpsEmployees;
          break;
      }

      if (event.previousIndex === event.currentIndex) {
        return; // No reordering needed
      }

      // Store for revert on cancel
      // For multi-channel, we need to store as NormalizedEmployee[] with a deep copy
      // that includes the channels property for proper restoration
      this.drag_event = event as CdkDragDrop<Employee[] | undefined>;
      this.old_active = employeeList.map(emp => JSON.parse(JSON.stringify(emp))) as any;

      // Reorder the employee list
      moveItemInArray(employeeList, event.previousIndex, event.currentIndex);
      
      // Show authorization modal
      $('#modal_drag_reorder').modal('show');
      return;
    }

    // Legacy single-column drag-drop (backward compatibility)
    if (!this.active) return;
    this.drag_event = event as CdkDragDrop<Employee[] | undefined>;
    this.old_active = [];

    for (var i = 0; i < this.active.length; i++) {
      copyArrayItem(this.active, this.old_active, i, i);
    }
    moveItemInArray(
      this.active,
      this.drag_event.previousIndex,
      this.drag_event.currentIndex
    );
    $('#modal_drag_reorder').modal('show');
  }

  /**
   * Authorizes and performs queue reorder operation.
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * All affected employees are upgraded to new format during this operation.
   * Handles both legacy single-column and multi-channel reordering.
   * 
   * @param adminCode Admin authorization code
   * @param channel Channel to reorder queue for (uses currentDragChannel as fallback)
   */
  async authorizeReorder(adminCode: string, channel?: ChannelType) {
    if (!this.drag_event || !this.boardId) return;

    const isLegal = await this.authTokenSvc.isLegalAuthToken(
      this.boardId,
      adminCode
    );

    if (!isLegal) {
      this.modal_error = 'Invalid Admin Code!';
      return;
    }

    try {
      // Determine which list to update based on whether this is multi-channel
      let listToUpdate: NormalizedEmployee[] | Employee[] = [];
      // Use currentDragChannel which was set during drop() - this is the reliable source
      let reorderChannel: ChannelType = channel || this.currentDragChannel || 'instore';

      // Multi-channel reordering (Story 3.1)
      // Check if the drag event data contains NormalizedEmployee (multi-channel mode)
      if (this.drag_event.container.data && this.drag_event.container.data.length > 0) {
        const firstItem = this.drag_event.container.data[0];
        
        // Check if this is a NormalizedEmployee (has channels property)
        if ((firstItem as any).channels) {
          // Multi-channel mode: use currentDragChannel which was set during drop()
          // This is more reliable than trying to match arrays after reorder
          switch (reorderChannel) {
            case 'phone':
              listToUpdate = this.phoneUpsEmployees;
              break;
            case 'online':
              listToUpdate = this.internetUpsEmployees;
              break;
            case 'instore':
            default:
              listToUpdate = this.floorUpsEmployees;
          }
        } else {
          // Legacy single-column mode
          listToUpdate = this.active || [];
        }
      } else {
        // Legacy single-column mode
        listToUpdate = this.active || [];
      }

      // Update orders for all employees in the list
      const updates: Promise<void>[] = listToUpdate.map(async (emp: any, index: number) => {
        if (!emp.email || !this.boardId) return;
        
        // 1. NORMALIZE: Convert legacy format if needed
        const normalized = normalizeEmployee(emp as unknown as RawEmployee);
        
        // 2. MODIFY: Update order for this channel
        normalized.channels[reorderChannel].order = index;
        
        // 3. DENORMALIZE & WRITE: Save to Firestore (upgrades all employees permanently)
        const forWrite = denormalizeForWrite(normalized);
        await this.db.collection('boards').doc(this.boardId!)
          .collection('users').doc(normalized.email)
          .set(forWrite, { merge: true });
      });
      
      await Promise.all(updates);
    } catch (error: any) {
      console.error('[BoardTransaction] Queue reorder failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
      // Revert optimistic UI change by restoring old_active
      if (this.old_active) {
        this.active = this.old_active;
        this.old_active = null;
      }
      return;
    }

    this.old_active = null;
    $('#modal_drag_reorder').modal('hide');
  }

  sortInactive(emps: Employee[] | undefined) {
    const inactive: Employee[] = [];
    const active = this.active ?? [];

    emps?.forEach((a) => {
      const index = active.findIndex((b) => {
        return b.email === a.email;
      });

      if (index === -1) {
        inactive.push(a);
      }
    });

    return this.unique(inactive).sort((a, b) => {
      var na = a.name || a.email || '';
      var nb = b.name || b.email || '';
      return na.localeCompare(nb);
    });
  }

  uniqueReal(emps: Employee[] | undefined, users: string[]) {
    const uniqueArray: Employee[] = [];

    emps?.forEach((a) => {
      const index = uniqueArray.findIndex((b) => {
        return b.email === a.email;
      });

      const uIndex = users.findIndex((b) => {
        return b === a.email;
      });

      const add = index === -1;
      const real = uIndex >= 0;
      if (add && real) {
        uniqueArray.push(a);
      }
    });

    return uniqueArray;
  }

  unique(emps: Employee[] | undefined) {
    const uniqueArray: Employee[] = [];

    emps?.forEach((a) => {
      const index = uniqueArray.findIndex((b) => {
        return b.email === a.email;
      });

      if (index === -1) {
        uniqueArray.push(a);
      }
    });

    return uniqueArray;
  }
  /**
   * Cancels a drag-and-drop reorder operation and restores the original order.
   * Works for both legacy single-column and multi-channel reordering.
   * Properly triggers change detection for Angular to update the UI.
   */
  cancelReorder() {
    // Multi-channel support: restore the appropriate list based on currentDragChannel
    const reorderChannel = this.currentDragChannel || 'instore';
    let targetList: NormalizedEmployee[] | Employee[] | undefined;
    
    switch (reorderChannel) {
      case 'phone':
        targetList = this.phoneUpsEmployees;
        break;
      case 'online':
        targetList = this.internetUpsEmployees;
        break;
      case 'instore':
      default:
        // For instore, check if we're in multi-channel mode (floorUpsEmployees) or legacy mode (active)
        targetList = this.floorUpsEmployees && this.floorUpsEmployees.length > 0 ? this.floorUpsEmployees : this.active;
    }
    
    if (!targetList || !this.old_active) {
      this.modal_error = undefined;
      return;
    }
    
    // Restore the old order by clearing and rebuilding the target list
    // This ensures all items are restored in their original sequence
    (targetList as any[]).length = 0;
    
    // Push all items from old_active back into the target list
    // old_active was created during drop() and contains the original order
    this.old_active.forEach((emp: Employee | NormalizedEmployee) => {
      (targetList as any[]).push(emp);
    });
    
    // Clear backup and trigger change detection to update the UI
    this.old_active = null;
    this.modal_error = undefined;
    this.cdr.markForCheck();
  }


  /**
   * Sends an employee to delivery (vehicle delivery state).
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * Note: Delivery states are ONLY valid for instore channel.
   * Validates state is valid before transition.
   * 
   * @param emp Employee to send on delivery (may be legacy or new format)
   */
  async sendOnDelivery(emp: Employee) {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[sendOnDelivery] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[sendOnDelivery] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[sendOnDelivery] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // Determine target state based on current instore state
      const currentState = normalized.channels.instore.state;
      let targetState: 'with_guest_delivery' | 'with_apt_delivery';
      
      if (currentState === 'with_guest') {
        targetState = 'with_guest_delivery';
      } else if (currentState === 'with_apt') {
        targetState = 'with_apt_delivery';
      } else {
        // Invalid transition - delivery only from with_guest or with_apt
        return;
      }

      // 3. VALIDATE: Delivery states are only valid for instore channel
      if (!isValidStateForChannel(targetState, 'instore')) {
        console.error('[BoardTransaction] Invalid state for channel:', targetState);
        return;
      }

      // 4. MODIFY: Set delivery state on instore channel
      console.log(`[sendOnDelivery] MODIFY: Setting channels.instore.state to ${targetState}`);
      normalized.channels.instore.state = targetState;

      // 5. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[sendOnDelivery] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[sendOnDelivery] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] sendOnDelivery() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  /**
   * Returns an employee from delivery back to available state.
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * @param emp Employee returning from delivery (may be legacy or new format)
   */
  async returnFromDelivery(emp: Employee) {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[returnFromDelivery] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[returnFromDelivery] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[returnFromDelivery] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // Verify employee is in a delivery state before transitioning
      const currentState = normalized.channels.instore.state;
      if (currentState !== 'with_guest_delivery' && currentState !== 'with_apt_delivery') {
        return;
      }

      // 3. MODIFY: Restore instore channel to available
      console.log(`[returnFromDelivery] MODIFY: Setting channels.instore.state to available`);
      normalized.channels.instore.state = 'available';

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[returnFromDelivery] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[returnFromDelivery] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] returnFromDelivery() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  /**
   * Sends an employee on break.
   * Break is GLOBAL - affects all channels simultaneously.
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * Queue positions (order) are PRESERVED during break - employee retains position
   * when returning from break.
   * 
   * @param emp Employee to send on break (may be legacy or new format)
   */
  async goOnBreak(emp: Employee) {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[goOnBreak] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[goOnBreak] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[goOnBreak] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Break is GLOBAL - affects all channels
      console.log(`[goOnBreak] MODIFY: Setting globalState to 'break' and instore channel state to 'break'`);
      normalized.globalState = 'break';
      
      // Story 3.5.4: Break is an ACTIVE status (employee in-store, temporarily unavailable)
      // Instore state becomes 'break' (not 'inactive') to keep employee in active section
      // Per-channel states remain 'available' - globalState controls availability
      normalized.channels.instore.state = 'break';
      // NOTE: phone/online channel states unchanged - globalState handles unavailability
      // NOTE: channels[*].order UNCHANGED - queue position preserved

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[goOnBreak] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[goOnBreak] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] goOnBreak() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  sendToBack(emp: Employee) {
    if (!this.active) {
      this.active = [];
    }

    for (let i = 0; i < this.active.length; i++) {
      if (this.active[i] === emp) {
        this.active.splice(i, 1);
        break;
      }
    }
    this.active.push(emp);
  }

  /**
   * Authorizes keeping an employee's place in line (returns them to available).
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * @param emp Employee to keep in line (may be legacy or new format)
   * @param adminCode Admin authorization code
   * @param channel Channel to return to available for (defaults to instore)
   */
  async authorizeKeepPlaceInLine(emp: Employee | undefined, adminCode: string, channel: ChannelType = 'instore') {
    if (!emp || !this.boardId || !emp.email) return;
    const isLegal = await this.authTokenSvc.isLegalAuthToken(
      this.boardId || '',
      adminCode
    );

    if (!isLegal) {
      this.modal_error = 'Invalid Admin Code!';
      return;
    }

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[authorizeKeepPlaceInLine] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[authorizeKeepPlaceInLine] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[authorizeKeepPlaceInLine] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Set channel state to available
      console.log(`[authorizeKeepPlaceInLine] MODIFY: Setting channels.${channel}.state to 'available'`);
      normalized.channels[channel].state = 'available';

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[authorizeKeepPlaceInLine] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      this.modal_error = undefined;
      
      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[authorizeKeepPlaceInLine] WRITE: Firestore update successful`);
      $('#modal_keep_spot').modal('hide');
    } catch (error: any) {
      console.error('[BoardTransaction] authorizeKeepPlaceInLine() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  /**
   * Enters an employee into the queue (makes them available).
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * @param emp Employee to enter into queue (may be legacy or new format)
   * @param sendToBack If true, sends employee to back of queue before entering
   * @param channel Channel to enter queue for (defaults to instore)
   */
  async enterQueue(emp: Employee, sendToBack: boolean, channel: ChannelType = 'instore') {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[enterQueue] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[enterQueue] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[enterQueue] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Update the normalized structure
      console.log(`[enterQueue] MODIFY: Setting channels.${channel}.state to 'available'`);
      normalized.channels[channel].state = 'available';
      
      // Clear global break state if employee was on break
      if (normalized.globalState === 'break') {
        console.log(`[enterQueue] MODIFY: Clearing globalState break`);
        normalized.globalState = null;
      }
      
      // If sending to back, set order to max + 1
      if (sendToBack && this.normalizedUsers.length > 0) {
        const maxOrder = Math.max(
          ...this.normalizedUsers.map(u => u.channels[channel].order)
        );
        console.log(`[enterQueue] MODIFY: Sending to back, setting order to ${maxOrder + 1}`);
        normalized.channels[channel].order = maxOrder + 1;
      }

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[enterQueue] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[enterQueue] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] enterQueue() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  makeInactive(emp: Employee) {
    this.nextInactiveEmployee = emp;
  }

  /**
   * Makes an employee inactive (removes from queue).
   * 
   * **Bug Fix (Story 3.5.3):** Fetches complete employee document from Firestore before
   * modification to ensure all fields (roles, smsNotify, all channels) are preserved
   * during the write operation. This prevents the employee from disappearing from other
   * channel columns due to missing fields in the write payload.
   * 
   * **Data Flow:**
   * 1. FETCH: Get complete employee document from Firestore (ensures all fields present)
   * 2. NORMALIZE: Convert legacy format if needed (idempotent for already-normalized data)
   * 3. MODIFY: Set channel state to inactive (only for specified channel)
   * 4. DENORMALIZE & WRITE: Save to Firestore with all fields preserved (merge:true)
   * 
   * **Multi-Channel State Independence:** Setting channels[channel].state = 'inactive'
   * only affects the specified channel. Other channels (phone, online) remain unchanged.
   * This is critical for AC-4: "Setting channels.instore.state = 'inactive' does NOT
   * affect channels.phone.state or channels.online.state"
   * 
   * @param emp Employee to make inactive (may be legacy or new format - only email is used)
   * @param channel Channel to make inactive for (defaults to instore)
   */
  async authorizeMakeInactive(emp: Employee | undefined, channel: ChannelType = 'instore') {
    if (!emp || !this.boardId || !emp.email) return;

    if (!this.active) {
      this.active = [];
    }
    if (!this.inactive) {
      this.inactive = [];
    }

    // Update local arrays for optimistic UI
    for (let i = 0; i < this.active.length; i++) {
      if (this.active[i] === emp) {
        this.active.splice(i, 1);
        break;
      }
    }
    this.inactive.push(emp);

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[authorizeMakeInactive] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[authorizeMakeInactive] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[authorizeMakeInactive] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

       // 3. MODIFY: Set globalState to inactive (channels stay as-is)
       // Story 5.3: Make Inactive sets only globalState, not per-channel states
       // Channel states are NOT modified - they preserve their "ready state"
       // Queue positions (order) are also preserved for when employee returns
       console.log(`[authorizeMakeInactive] MODIFY: Setting globalState to 'inactive'`);
       normalized.globalState = 'inactive';

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      console.log(`[authorizeMakeInactive] WRITE: Preparing denormalized employee for Firestore write`);
      const forWrite = denormalizeForWrite(normalized);
      
      // Verify all critical fields are in the write payload
      console.log(`[authorizeMakeInactive] WRITE payload includes:`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasChannels: !!forWrite.channels,
        hasPhoneChannel: !!forWrite.channels?.phone,
        hasOnlineChannel: !!forWrite.channels?.online,
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[authorizeMakeInactive] SUCCESS: Employee ${emp.email} marked inactive in ${channel} channel`);
    } catch (error: any) {
      console.error('[authorizeMakeInactive] FAILED:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  makeActive(emp: Employee) {
    if (!this.inactive) return;

    for (let i = 0; i < this.inactive.length; i++) {
      if (this.inactive[i] === emp) {
        this.inactive.splice(i, 1);
        break;
      }
    }
    this.enterQueue(emp, true);
  }

  /**
   * Story 3.5.4: Show context menu for inactive employee on right-click.
   * Prevents default context menu and displays "Make Active" option.
   * 
   * @param event - Mouse context menu event
   * @param emp - Inactive employee to act upon
   */
  showInactiveContextMenu(event: MouseEvent, emp: NormalizedEmployee) {
    event.preventDefault();
    event.stopPropagation();
    
    // Toggle context menu visibility (click elsewhere to hide)
    this.activeContextMenuEmail = this.activeContextMenuEmail === emp.email ? null : emp.email;
  }

  /**
   * Story 3.5.4: Make an inactive employee active (available).
   * Transitions employee from inactive to available state and moves to active section.
   * 
   * @param emp - Inactive employee to activate
   */
  async makeEmployeeActive(emp: NormalizedEmployee) {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[makeEmployeeActive] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;

      // 2. NORMALIZE: Convert to new format with all fields preserved
      console.log(`[makeEmployeeActive] NORMALIZE: Converting to new format`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Set state to available for all channels and send to bottom of all queues
      console.log(`[makeEmployeeActive] MODIFY: Setting all channel states to 'available'`);
      normalized.channels.instore.state = 'available';
      normalized.channels.phone.state = 'available';
      normalized.channels.online.state = 'available';
      normalized.globalState = null; // Clear break or inactive state (Story 5.3)
      
      // Send to bottom of all queues (instore, phone, online)
      // Find max order for each channel and set order to max + 1
      if (this.normalizedUsers.length > 0) {
        const maxInstoreOrder = Math.max(...this.normalizedUsers.map(u => u.channels.instore.order));
        const maxPhoneOrder = Math.max(...this.normalizedUsers.map(u => u.channels.phone.order));
        const maxOnlineOrder = Math.max(...this.normalizedUsers.map(u => u.channels.online.order));
        
        normalized.channels.instore.order = maxInstoreOrder + 1;
        normalized.channels.phone.order = maxPhoneOrder + 1;
        normalized.channels.online.order = maxOnlineOrder + 1;
        
        console.log(`[makeEmployeeActive] MODIFY: Sending to bottom of all queues - instore: ${maxInstoreOrder + 1}, phone: ${maxPhoneOrder + 1}, online: ${maxOnlineOrder + 1}`);
      }

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[makeEmployeeActive] WRITE: Updating employee document`);

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[makeEmployeeActive] SUCCESS: Employee ${emp.email} is now active`);
      
      // Close context menu
      this.activeContextMenuEmail = null;
    } catch (error: any) {
      console.error('[makeEmployeeActive] failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  /**
   * Close context menu when clicking elsewhere (called from template)
   */
  closeContextMenu() {
    this.activeContextMenuEmail = null;
  }

  /**
   * Sends an employee with an appointment guest.
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * Note: Appointments are instore-only operations.
   * 
   * @param emp Employee to send with appointment (may be legacy or new format)
   */
  async sendOnApt(emp: Employee) {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[sendOnApt] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[sendOnApt] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[sendOnApt] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Appointments are instore-only
      console.log(`[sendOnApt] MODIFY: Setting channels.instore.state to 'with_apt'`);
      normalized.channels.instore.state = 'with_apt';

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[sendOnApt] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[sendOnApt] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] sendOnApt() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  /**
   * Returns an employee from break, restoring them to available state.
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * @param emp Employee returning from break (may be legacy or new format)
   * @param channel Channel to return to (defaults to instore)
   */
  async returnFromBreak(emp: Employee, channel: ChannelType = 'instore') {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[returnFromBreak] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[returnFromBreak] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[returnFromBreak] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Clear global break state and restore ALL channels to available
      // Story 3.5.5: Return from Break makes employee active in ALL channels
      console.log(`[returnFromBreak] MODIFY: Clearing globalState and setting ALL channels to 'available'`);
      normalized.globalState = null;
      normalized.channels.instore.state = 'available';
      normalized.channels.phone.state = 'available';
      normalized.channels.online.state = 'available';

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[returnFromBreak] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[returnFromBreak] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] returnFromBreak() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  /**
   * Sends an employee with a walk-in guest.
   * Uses normalize → modify → denormalize pattern for auto-upgrade (Story 1.6).
   * 
   * @param emp Employee to send with guest (may be legacy or new format)
   * @param channel Channel the guest came from (defaults to instore)
   */
  async sendWithGuest(emp: Employee, channel: ChannelType = 'instore') {
    if (!this.boardId || !emp.email) return;

    try {
      // 1. FETCH: Get complete employee document from Firestore
      console.log(`[sendWithGuest] FETCH: Loading complete employee document for ${emp.email}`);
      const empDoc = await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(emp.email)
        .get()
        .toPromise();

      if (!empDoc || !empDoc.exists) {
        throw new Error(`Employee document not found: ${emp.email}`);
      }

      const empData = empDoc.data() as RawEmployee;
      console.log(`[sendWithGuest] FETCH complete:`, { email: empData.email, hasChannels: !!empData.channels, hasRoles: !!empData.roles });

      // 2. NORMALIZE: Convert legacy format if needed (with complete data)
      console.log(`[sendWithGuest] NORMALIZE: Converting employee data (legacy: ${!empData.channels})`);
      const normalized = normalizeEmployee(empData);

      // 3. MODIFY: Update the normalized structure
      console.log(`[sendWithGuest] MODIFY: Setting channels.${channel}.state to 'with_guest'`);
      normalized.channels[channel].state = 'with_guest';

      // 4. DENORMALIZE & WRITE: Save to Firestore (all fields preserved)
      const forWrite = denormalizeForWrite(normalized);
      console.log(`[sendWithGuest] WRITE: Verifying write payload has all fields`, {
        hasRoles: !!forWrite.roles,
        hasSmsNotify: forWrite.smsNotify !== undefined,
        hasPhoneChannel: !!forWrite.channels.phone,
        hasOnlineChannel: !!forWrite.channels.online
      });

      await this.db.collection('boards').doc(this.boardId)
        .collection('users').doc(normalized.email)
        .set(forWrite, { merge: true });

      console.log(`[sendWithGuest] WRITE: Firestore update successful`);
    } catch (error: any) {
      console.error('[BoardTransaction] sendWithGuest() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

  async save() {
    if (!this.boardId) {
      return;
    }

    if (!this.active) this.active = [];
    if (!this.inactive) this.inactive = [];
    this.setUpAndOnDeck(this.active);

    try {
      await this.transactionBoardSvc.saveBoard(
        this.boardId,
        this.active,
        this.inactive,
        {
          up: this.up?.email ?? '',
          onDeck: this.onDeck?.email ?? '',
        },
        { logPrefix: 'BoardSave' }
      );
    } catch (error: any) {
      console.error('[BoardTransaction] save() failed:', error);
      const userMessage = getUserFriendlyMessage(error);
      this.notificationSvc.showError(userMessage);
    }
  }

   ngOnInit(): void {
     var sub = this.auth.user.subscribe((u) => {
       sub.unsubscribe();

       if (!u || !u.email) return;
       this.boardId = this.route.snapshot.paramMap.get('id');
       if (!this.boardId) return;

       const userEmail = u.email; // Capture email to ensure it's not null
       
       // Initialize board permissions service for admin checks
       this.boardPermissionsService.init(this.boardId, userEmail);
       
       // Initialize tutorial state subscription immediately
       // This ensures state is loaded before we try to filter tutorials
       this.tutorialStateService.init(this.boardId, userEmail);

// Subscribe to board document for config
          this.db
            .collection('boards')
            .doc<any>(this.boardId)
            .valueChanges()
            .pipe(takeUntil(this.destroy$))
            .subscribe((boardDoc) => {
              // Access config with safe navigation and default
              this.boardConfig = boardDoc?.config || {};
              // Deliveries are an instore feature - only show if instore channel is purchased AND enabled AND showDelivery is enabled
              const instorePurchased = boardDoc?.config?.instoreChannelPurchased !== false;
              const instoreEnabled = boardDoc?.config?.enableInstoreChannel !== false;
              const deliveryEnabled = boardDoc?.config?.showDelivery !== false;
              this.showDelivery = instorePurchased && instoreEnabled && deliveryEnabled;
              
              // DEBUG: Log boardConfig for Story 3.9 debugging
              console.log('[DEBUG Story 3.9] boardConfig received:', JSON.stringify(this.boardConfig));
              console.log('[DEBUG Story 3.9] useModernLayout value:', this.boardConfig?.useModernLayout);
              console.log('[DEBUG Story 3.9] isLegacyMode result:', this.isLegacyMode);
              
              // Update enabled channels based on config (Story 3.1)
              this.updateEnabledChannels(this.boardConfig);
              
              // Trigger tutorials when board config loads (after feature gates are known)
              this.triggerInitialTutorial(boardDoc, userEmail);
              
              // Trigger change detection for OnPush strategy (Task 7: Change Detection Optimization)
              this.cdr.markForCheck();
            });

        this.db
          .collection('boards')
          .doc(this.boardId)
          .collection<BoardUserData>('users')
          .valueChanges({ idField: 'email' })
          .pipe(takeUntil(this.destroy$))
          .subscribe((usrs) => {
            // Store raw users for backward compatibility
            this.users = usrs.map(u => {
              u.state = u.state || "inactive";
              return u;
            });
            
            // Store previous normalized users for change detection (Story 3.6)
            const previousUsers = this.normalizedUsers;
            
            // NEW: Normalize all users (handles mixed old/new formats) - Story 1.6
            this.normalizedUsers = normalizeEmployees(usrs as unknown as RawEmployee[]);
            
            // Detect changes for animations (Story 3.6)
            if (previousUsers.length > 0) {
              this.detectAndAnimateChanges(previousUsers, this.normalizedUsers);
            }
            
            this.updateBoardState();
            this.updateEmployeeLists(); // Compute column-specific employee arrays
            
            // Update badge animations after employee lists are updated (Story 3.6)
            this.updateBadgeAnimations();
            
            // Trigger change detection for OnPush strategy (Task 7: Change Detection Optimization)
            this.cdr.markForCheck();
          });

        // Listen for route changes to detect when user returns from admin routes
        // Reset tutorial flag so it can trigger again on the main board view
        this.router.events
          .pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
          )
          .subscribe((event: any) => {
            const url = event.url;
            
            // If we're back on the main board view (not in admin routes), reset tutorial flag
            // This allows the tutorial to show again when returning from admin config
            if (url.match(`^/boards/[^/]+/?$`)) {
              console.log(`[Tutorial Debug] Returned to main board view, resetting tutorial flag`);
              this.initialTutorialTriggered = false;
              
              // Trigger change detection for OnPush strategy (Task 7: Change Detection Optimization)
              this.cdr.markForCheck();
              
              // Trigger the tutorial again if conditions are met
              if (this.boardId) {
                this.db
                  .collection('boards')
                  .doc<any>(this.boardId)
                  .valueChanges()
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((boardDoc) => {
                    this.triggerInitialTutorial(boardDoc, userEmail);
                    
                    // Trigger change detection for OnPush strategy (Task 7: Change Detection Optimization)
                    this.cdr.markForCheck();
                  });
              }
            }
          });
      });
   }

  /**
   * Clean up resources when component is destroyed.
   * Ensures all subscriptions are cleaned up to prevent memory leaks (Story 3.6).
   */
  ngOnDestroy(): void {
    // Emit to complete all subscriptions using takeUntil pattern (Story 3.6)
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up tutorial state subscription
    this.tutorialStateService.cleanup();
  }
   
   /**
     * Trigger the first incomplete tutorial for the user on this board.
     * Called when board config loads to ensure feature gates are initialized.
     * 
     * Only triggers once per session to avoid showing tutorials repeatedly
     * when board config changes (e.g., when toggling feature flags).
     * 
     * Waits for tutorial state to be loaded from Firestore before filtering tutorials.
     * This prevents the race condition where tutorials are shown even if they've been
     * completed/dismissed on another device.
     * 
     * @param boardDoc The board document with config
     * @param userEmail The current user's email
     */
   private async triggerInitialTutorial(boardDoc: any, userEmail: string): Promise<void> {
     // Only trigger once per session - don't re-show tutorials when board config changes
     if (this.initialTutorialTriggered) {
       return;
     }
     
     if (!this.boardId || !userEmail) return;
     
      try {
        // Wait for tutorial state to be loaded from Firestore
        // This is critical to prevent showing tutorials that were already completed
        await this.tutorialStateService.waitForInitialization();
        
        // Mark as triggered so we don't re-run on subsequent board config changes
        this.initialTutorialTriggered = true;
        
        // Get all applicable tutorials for this board based on feature gates
        const availableTutorials = this.tutorialService.getTutorialsForBoard(boardDoc);
        console.log(`[Tutorial Debug] Board config:`, boardDoc?.config);
        console.log(`[Tutorial Debug] Available tutorials:`, availableTutorials.map(t => t.id));
        
        // Filter out tutorials the user has already completed or dismissed
        const incompleteTutorials = this.tutorialService.filterCompletedTutorials(availableTutorials);
        console.log(`[Tutorial Debug] Incomplete tutorials:`, incompleteTutorials.map(t => t.id));
        
        // Show the first incomplete tutorial if any exist
        if (incompleteTutorials.length > 0) {
          const firstTutorial = incompleteTutorials[0];
          const userIsAdmin = this.boardPermissionsService.userHasAdminPermissions(
            this.boardPermissionsService.boardUser
          ) || false;
          console.log(`[Tutorial] Showing ${firstTutorial.id} tutorial to user on board ${this.boardId} (admin: ${userIsAdmin})`);
          this.tutorialModalService.showTutorial(firstTutorial, this.boardId, userEmail, userIsAdmin);
        } else {
          console.log(`[Tutorial Debug] No incomplete tutorials to show`);
        }
      } catch (error) {
        console.error('[Tutorial] Error triggering initial tutorial:', error);
        // Silently fail - tutorials are nice-to-have, don't block board operations
      }
   }
  
   /**
    * Updates enabled channels array based on board config feature flags (Story 3.1).
    * Conditionally includes 'instore', 'phone', and 'online' based on config.
    * 
    * A channel is only enabled if it is BOTH:
    * 1. Purchased by super-admin (xxxChannelPurchased flag)
    * 2. Enabled by board admin (enableXxxChannel flag)
    * 
    * Note: enableInstoreChannel and instoreChannelPurchased default to true for backward compatibility
    * (Walk-ins is the original/core functionality).
    * 
    * @param boardConfig Board configuration with purchased and enable flags for each channel
    */
   updateEnabledChannels(boardConfig: any): void {
     this.enabledChannels = [];
     
     // Instore: purchased defaults to true (backward compat), enabled defaults to true
     const instorePurchased = boardConfig?.instoreChannelPurchased !== false;
     const instoreEnabled = boardConfig?.enableInstoreChannel !== false;
     if (instorePurchased && instoreEnabled) {
       this.enabledChannels.push('instore');
     }
     
     // Phone: must be BOTH purchased AND enabled
     const phonePurchased = boardConfig?.phoneChannelPurchased === true;
     const phoneEnabled = boardConfig?.enablePhoneChannel === true;
     if (phonePurchased && phoneEnabled) {
       this.enabledChannels.push('phone');
     }
     
     // Online: must be BOTH purchased AND enabled
     const onlinePurchased = boardConfig?.internetChannelPurchased === true;
     const onlineEnabled = boardConfig?.enableOnlineChannel === true;
     if (onlinePurchased && onlineEnabled) {
       this.enabledChannels.push('online');
     }
   }

   /**
    * Filters employees into per-channel lists for multi-column display (Story 3.1).
    * Computes column-specific arrays based on state and eligibility rules.
    * 
    * Column Filtering Rules:
    * - Floor Ups: All EXCEPT with_guest/with_guest_delivery (instore channel)
    * - With Guest: ONLY with_guest or with_guest_delivery (instore channel)
    * - Phone Ups: All eligible for phone channel
    * - Internet Ups: All eligible for online channel
    */
    updateEmployeeLists(): void {
      // First, calculate up and on deck for legacy UI (current channel)
      const { up, onDeck } = calculateUpAndOnDeck(
        this.users as unknown as RawEmployee[],
        this.currentChannel
      );
      
      // Convert normalized employees back to Employee format for UI compatibility
      // This allows gradual migration - UI still uses legacy format
      if (up) {
        this.up = this.normalizedToEmployee(up);
      } else {
        this.up = undefined;
      }
      
      if (onDeck) {
        this.onDeck = this.normalizedToEmployee(onDeck);
      } else {
        this.onDeck = undefined;
      }

      // NEW: Compute multi-channel per-column employee lists
      // Normalize all users first (handles mixed old/new formats)
      const normalized = this.normalizedUsers;

       // **Floor Ups Column**: Available, with_apt, with_apt_delivery, break
       // INCLUDES: break (Story 3.5.4 - break is ACTIVE, just temporarily unavailable)
       // EXCLUDES: with_guest, with_guest_delivery, inactive
       this.floorUpsEmployees = normalized
          .filter(emp => {
            const state = emp.channels.instore.state;
            // Exclude with_guest states (those go to With Guest column)
            // Exclude globally inactive employees (Story 5.3: use globalState check)
            // INCLUDE break (break is ACTIVE per Story 3.5.4)
            return state !== 'with_guest' && 
                   state !== 'with_guest_delivery' && 
                   !isEmployeeInactive(emp);
          })
          .sort((a, b) => a.channels.instore.order - b.channels.instore.order);

      // **With Guest Column**: ONLY with_guest or with_guest_delivery (instore)
      this.withGuestEmployees = normalized
        .filter(emp => {
          const state = emp.channels.instore.state;
          return state === 'with_guest' || state === 'with_guest_delivery';
        })
        .sort((a, b) => a.channels.instore.order - b.channels.instore.order);

      // **Phone Ups Column**: All eligible for phone channel (excluding globally inactive)
      // Story 5.3: Use isEmployeeInactive() instead of per-channel inactive check
      this.phoneUpsEmployees = normalized
        .filter(emp => emp.channels.phone.eligible && !isEmployeeInactive(emp))
        .sort((a, b) => a.channels.phone.order - b.channels.phone.order);

      // **Internet Ups Column**: All eligible for online channel (excluding globally inactive)
      // Story 5.3: Use isEmployeeInactive() instead of per-channel inactive check
      this.internetUpsEmployees = normalized
        .filter(emp => emp.channels.online.eligible && !isEmployeeInactive(emp))
        .sort((a, b) => a.channels.online.order - b.channels.online.order);

       // **Inactive Employees Section** (Story 3.5.1): Separate section, sorted alphabetically by name
       // Story 5.3: Use globalState === 'inactive' (not per-channel check)
       // Exclude break employees - they are ACTIVE, not inactive
       this.inactiveEmployees = normalized
         .filter(emp => isEmployeeInactive(emp))
         .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      // **Story 3.6 - Badge Visibility Fix: Calculate UP/OnDeck per channel**
      // IMPORTANT: We must calculate UP/OnDeck from the same normalized arrays used for filtering
      // to ensure object references match in template comparisons (emp === upInstore)
      
      // Instore channel (Floor Ups and With Guest columns share same UP/OnDeck)
      // Get available employees for instore (those with state='available' and eligible)
      const instoreAvailable = normalized.filter(emp => {
        // Must be eligible for instore
        if (!emp.channels.instore.eligible) return false;
        // Story 5.3: Globally inactive employees should never be UP/OnDeck
        if (isEmployeeInactive(emp)) return false;
        // Must not be on global break
        if (this.isEmployeeOnBreak(emp)) return false;
        // Must be 'available' in this channel
        if (emp.channels.instore.state !== 'available') return false;
        // Must not be busy in any channel (cross-channel block)
        if (this.isEmployeeBusyInAnyChannel(emp)) return false;
        return true;
      }).sort((a, b) => a.channels.instore.order - b.channels.instore.order);
      this.upInstore = instoreAvailable[0];
      this.onDeckInstore = instoreAvailable[1];

      // Phone channel
      // FR-P3-003, FR-P3-004, FR-P3-005: Cross-channel UP/On Deck unavailability
      // Employees UP/OnDeck in instore are excluded from being UP/OnDeck in phone (unless config allows)
      const instorePurchased = this.boardConfig?.instoreChannelPurchased !== false;
      const instoreEnabled = this.boardConfig?.enableInstoreChannel !== false;
      const phoneAvailable = normalized.filter(emp => {
        // Must be eligible for phone
        if (!emp.channels.phone.eligible) return false;
        // Story 5.3: Globally inactive employees should never be UP/OnDeck
        if (isEmployeeInactive(emp)) return false;
        // Must not be on global break
        if (this.isEmployeeOnBreak(emp)) return false;
        // Must be 'available' in this channel
        if (emp.channels.phone.state !== 'available') return false;
        // Must not be busy in any channel (cross-channel block)
        if (this.isEmployeeBusyInAnyChannel(emp)) return false;
        // Cross-channel rules: exclude employees UP/OnDeck in instore (when applicable)
        if (instorePurchased && instoreEnabled) {
          const isUpForInstore = this.upInstore?.email === emp.email;
          const isOnDeckForInstore = this.onDeckInstore?.email === emp.email;
          // UP in instore is unavailable for phone by default
          if (isUpForInstore && !this.boardConfig?.allowUpToTakePhone) return false;
          // OnDeck in instore is available for phone by default (unless explicitly disabled)
          if (isOnDeckForInstore && this.boardConfig?.allowOnDeckToTakePhone === false) return false;
        }
        return true;
      }).sort((a, b) => a.channels.phone.order - b.channels.phone.order);
      this.upPhone = phoneAvailable[0];
      this.onDeckPhone = phoneAvailable[1];

      // Online channel
      // FR-P3-003, FR-P3-004, FR-P3-005: Cross-channel UP/On Deck unavailability
      // Employees UP/OnDeck in instore are excluded from being UP/OnDeck in online (unless config allows)
      const onlineAvailable = normalized.filter(emp => {
        // Must be eligible for online
        if (!emp.channels.online.eligible) return false;
        // Story 5.3: Globally inactive employees should never be UP/OnDeck
        if (isEmployeeInactive(emp)) return false;
        // Must not be on global break
        if (this.isEmployeeOnBreak(emp)) return false;
        // Must be 'available' in this channel
        if (emp.channels.online.state !== 'available') return false;
        // Must not be busy in any channel (cross-channel block)
        if (this.isEmployeeBusyInAnyChannel(emp)) return false;
        // Cross-channel rules: exclude employees UP/OnDeck in instore (when applicable)
        if (instorePurchased && instoreEnabled) {
          const isUpForInstore = this.upInstore?.email === emp.email;
          const isOnDeckForInstore = this.onDeckInstore?.email === emp.email;
          // UP in instore is unavailable for online by default
          if (isUpForInstore && !this.boardConfig?.allowUpToTakeInternet) return false;
          // OnDeck in instore is available for online by default (unless explicitly disabled)
          if (isOnDeckForInstore && this.boardConfig?.allowOnDeckToTakeInternet === false) return false;
        }
        return true;
      }).sort((a, b) => a.channels.online.order - b.channels.online.order);
      this.upOnline = onlineAvailable[0];
      this.onDeckOnline = onlineAvailable[1];
    }

   // =============================================
   // Real-Time Animation Methods (Story 3.6)
   // =============================================

   /**
    * Detects changes between previous and current employee lists and triggers animations (Story 3.6).
    * Compares state changes, column transitions, and new employees to determine appropriate animations.
    * 
    * @param previous Previous normalized employee list
    * @param current Current normalized employee list
    */
   detectAndAnimateChanges(
     previous: NormalizedEmployee[],
     current: NormalizedEmployee[]
   ): void {
     const previousMap = new Map(previous.map(e => [e.email, e]));
     const currentMap = new Map(current.map(e => [e.email, e]));
     
     const changes: EmployeeChange[] = [];
     
     // Detect state changes and column transitions
     current.forEach(emp => {
       const prev = previousMap.get(emp.email);
       
       if (!prev) {
         // New employee - entry animation
         changes.push({ email: emp.email, type: 'column_entry' });
       } else {
         // Check for state changes per channel
         if (prev.channels.instore.state !== emp.channels.instore.state) {
           changes.push({
             email: emp.email,
             type: 'state_change',
             fromState: prev.channels.instore.state,
             toState: emp.channels.instore.state
           });
           
           // Check for column transition (e.g., floor_ups -> with_guest)
           const wasWithGuest = prev.channels.instore.state === 'with_guest' || 
                               prev.channels.instore.state === 'with_guest_delivery';
           const isWithGuest = emp.channels.instore.state === 'with_guest' || 
                              emp.channels.instore.state === 'with_guest_delivery';
           
           if (wasWithGuest !== isWithGuest) {
             changes.push({
               email: emp.email,
               type: isWithGuest ? 'column_entry' : 'column_exit',
               fromColumn: wasWithGuest ? 'with_guest' : 'floor_ups',
               toColumn: isWithGuest ? 'with_guest' : 'floor_ups'
             });
           }
         }
         
         // Check for phone channel state changes
         if (this.enabledChannels.includes('phone') &&
             prev.channels.phone.state !== emp.channels.phone.state) {
           changes.push({
             email: emp.email,
             type: 'state_change',
             fromState: prev.channels.phone.state,
             toState: emp.channels.phone.state
           });
         }
         
         // Check for online channel state changes
         if (this.enabledChannels.includes('online') &&
             prev.channels.online.state !== emp.channels.online.state) {
           changes.push({
             email: emp.email,
             type: 'state_change',
             fromState: prev.channels.online.state,
             toState: emp.channels.online.state
           });
         }
       }
     });
     
     // Apply animation classes
     this.applyAnimations(changes);
   }

   /**
    * Applies animation CSS classes to employees based on detected changes (Story 3.6).
    * Sets animation class in the employeeAnimations Map and schedules removal after animation completes.
    * 
    * @param changes Array of detected employee changes
    */
   applyAnimations(changes: EmployeeChange[]): void {
     changes.forEach(change => {
       let animationClass = '';
       let duration = 300; // Default animation duration in ms
       
       switch (change.type) {
         case 'state_change':
           // Animation disabled - no pulsing border on state changes
           return;
         case 'column_entry':
           animationClass = 'entering-column';
           duration = 300; // slideUp animation duration
           break;
         case 'column_exit':
           animationClass = 'leaving-column';
           duration = 200; // fadeOut animation duration
           break;
         case 'position_change':
           animationClass = 'position-changed';
           duration = 300;
           break;
       }
       
       this.employeeAnimations.set(change.email, animationClass);
       
       // Remove animation class after animation completes
       setTimeout(() => {
         this.employeeAnimations.delete(change.email);
         this.cdr.markForCheck();
       }, duration);
     });
   }

   /**
    * Gets the current animation class for an employee (Story 3.6).
    * Used in template for class binding.
    * 
    * @param email Employee email to get animation class for
    * @returns Animation CSS class or empty string
    */
   getAnimationClass(email: string): string {
     return this.employeeAnimations.get(email) || '';
   }

   /**
    * Updates badge animations when UP or On Deck employee changes (Story 3.6).
    * Applies badge-arriving and badge-leaving classes to animate badge transitions.
    */
   updateBadgeAnimations(): void {
     const currentUp = this.up;
     const currentOnDeck = this.onDeck;
     
     // Detect UP change
     if (this.previousUpEmail !== currentUp?.email) {
       if (this.previousUpEmail) {
         // Animate badge leaving old employee
         this.employeeAnimations.set(this.previousUpEmail, 'badge-leaving');
         const prevEmail = this.previousUpEmail;
         setTimeout(() => {
           if (this.employeeAnimations.get(prevEmail) === 'badge-leaving') {
             this.employeeAnimations.delete(prevEmail);
             this.cdr.markForCheck();
           }
         }, 300);
       }
       if (currentUp?.email) {
         // Animate badge arriving at new employee
         this.employeeAnimations.set(currentUp.email, 'badge-arriving');
         const email = currentUp.email;
         setTimeout(() => {
           if (this.employeeAnimations.get(email) === 'badge-arriving') {
             this.employeeAnimations.delete(email);
             this.cdr.markForCheck();
           }
         }, 500);
       }
       this.previousUpEmail = currentUp?.email || null;
     }
     
     // Detect On Deck change
     if (this.previousOnDeckEmail !== currentOnDeck?.email) {
       if (this.previousOnDeckEmail) {
         this.employeeAnimations.set(this.previousOnDeckEmail, 'badge-leaving');
         const prevEmail = this.previousOnDeckEmail;
         setTimeout(() => {
           if (this.employeeAnimations.get(prevEmail) === 'badge-leaving') {
             this.employeeAnimations.delete(prevEmail);
             this.cdr.markForCheck();
           }
         }, 300);
       }
       if (currentOnDeck?.email) {
         this.employeeAnimations.set(currentOnDeck.email, 'badge-arriving');
         const email = currentOnDeck.email;
         setTimeout(() => {
           if (this.employeeAnimations.get(email) === 'badge-arriving') {
             this.employeeAnimations.delete(email);
             this.cdr.markForCheck();
           }
         }, 500);
       }
       this.previousOnDeckEmail = currentOnDeck?.email || null;
     }
   }

   /**
    * TrackBy function for ngFor performance optimization (Story 3.6).
    * Prevents unnecessary DOM recreation when employee lists update.
    * 
    * @param index Index in the array
    * @param employee Employee item
    * @returns Unique identifier (email)
    */
   trackByEmail(index: number, employee: NormalizedEmployee): string {
     return employee.email;
   }

   // =============================================
   // Employee Click Handler Methods (Story 3.5)
   // =============================================

   /**
    * Opens the employee action modal when an employee card is clicked.
    * Sets the selected employee and column, then shows the modal.
    * Skips showing the modal for inactive employees (they have no actions).
    * 
    * @param employee The normalized employee that was clicked
    * @param column The column where the click occurred
    */
   onEmployeeClick(employee: NormalizedEmployee, column: ColumnType): void {
     // Don't show action modal for inactive employees - they have no actions
     if (employee.globalState === 'inactive') {
       return;
     }
     this.selectedEmployee = employee;
     this.selectedColumn = column;
     this.showActionModal = true;
     this.cdr.markForCheck();
   }

    /**
     * Closes the employee action modal without executing any action.
     */
    closeActionModal(): void {
      this.showActionModal = false;
      this.selectedEmployee = null;
      this.cdr.markForCheck();
    }

    /**
     * Handles right-click context menu on employee cards.
     * Story 3.5.5: AC #1-#6 - Show context menu with state-specific actions.
     * 
     * Prevents default browser context menu and opens the action modal.
     * 
     * @param event The context menu event
     * @param employee The normalized employee that was right-clicked
     * @param column The column where the right-click occurred
     */
    onEmployeeContextMenu(event: MouseEvent, employee: NormalizedEmployee, column: ColumnType): void {
      // Prevent default browser context menu
      event.preventDefault();
      
      // Set up the modal with the employee and column context
      this.selectedEmployee = employee;
      this.selectedColumn = column;
      this.showActionModal = true;
      this.cdr.markForCheck();
    }

    /**
     * Executes an action from the employee action modal.
     * Story 3.5.5: Handles all state transitions including compound transitions.
     * 
     * @param action The action to execute
     */
    async executeAction(action: EmployeeAction): Promise<void> {
      const emp = this.normalizedToEmployee(action.employee);
      const channel = action.channel || 'instore';

      try {
        switch (action.type) {
         // Floor Ups actions
         case 'with_apt':
           await this.sendOnApt(emp);
           break;

         case 'with_guest':
           await this.sendWithGuest(emp, channel);
           break;

         case 'break':
           await this.goOnBreak(emp);
           break;

         case 'return_from_break':
           await this.returnFromBreak(emp, channel);
           break;

         case 'inactive':
           // Show confirmation modal instead of directly making inactive
           this.nextInactiveEmployee = emp;
           this.closeActionModal();
           $('#modal_remove_emp').modal('show');
           return; // Exit early - action will be completed when modal is confirmed

         case 'make_available':
           await this.enterQueue(emp, false, channel);
           break;

         // Delivery actions
         case 'delivery':
           await this.sendOnDelivery(emp);
           break;

         case 'complete_delivery_bottom':
           // Complete delivery and go to bottom of queue
           await this.enterQueue(emp, true, 'instore');
           break;

         case 'complete_delivery_keep':
           // Complete delivery but keep position (returns to available)
           await this.returnFromDelivery(emp);
           break;

         // Phone/Internet channel actions
         case 'take_phone':
           // Taking a phone lead puts employee in with_guest state for phone channel
           await this.sendWithGuest(emp, 'phone');
           break;

         case 'take_internet':
           // Taking an internet lead puts employee in with_guest state for online channel
           await this.sendWithGuest(emp, 'online');
           break;

          case 'go_to_bottom':
            // Go to bottom of the specific channel's queue
            await this.enterQueue(emp, true, channel);
            break;

          // Story 3.5.5: Complete appointment transitions
          case 'complete_apt':
            // Complete appointment and keep position in queue (like Return From Break)
            await this.enterQueue(emp, false, 'instore');
            break;

          case 'complete_apt_then_break':
            // Complete appointment (keep position), then go on break
            await this.enterQueue(emp, false, 'instore');
            await this.goOnBreak(emp);
            break;

          case 'go_to_bottom_then_break':
            // Go to bottom of floor queue, then go on break
            await this.enterQueue(emp, true, 'instore');
            await this.goOnBreak(emp);
            break;

          case 'reclaim_position':
            // Reclaim position requires admin authorization (matches legacy "Keep Place In Line")
            // Set modal_emp and open admin password modal
            this.modal_emp = emp;
            this.closeActionModal();
            $('#modal_keep_spot').modal('show');
            return; // Exit early - action will be completed when modal is confirmed

          default:
            console.warn('[Story 3.5] Unknown action type:', action.type);
        }
      } catch (error) {
        console.error('[Story 3.5] Error executing action:', error);
        this.notificationSvc.showError('Failed to execute action. Please try again.');
      }

      this.closeActionModal();
    }

   /**
    * Checks if an employee is unavailable for a specific channel (Story 3.1).
    * Employee is unavailable when:
    * - On global break
    * - Busy on ANY channel (cross-channel busy blocking)
    * - Not eligible for the channel
    * 
    * Used for visual graying in phone/online columns to show busy status.
    * 
    * @param emp Normalized employee to check
    * @param channel Channel to check availability for
    * @returns true if employee is unavailable for this channel
    */
   isEmployeeUnavailable(emp: NormalizedEmployee, channel: ChannelType): boolean {
     return isEmployeeUnavailable(emp, channel);
   }

    /**
     * Wrapper for utility function to check if employee is on break.
     * Checks the global 'break' state of the employee.
     * 
     * @param emp Normalized employee to check
     * @returns true if employee is on break globally
     */
    isEmployeeOnBreak(emp: NormalizedEmployee): boolean {
      return isEmployeeOnBreak(emp);
    }

    /**
     * Wrapper for utility function to check if employee is busy in any channel.
     * An employee is busy in a channel if their state is not 'available'.
     * 
     * @param emp Normalized employee to check
     * @returns true if employee is busy in any channel
     */
    isEmployeeBusyInAnyChannel(emp: NormalizedEmployee): boolean {
      return isEmployeeBusyInAnyChannel(emp);
    }

    /**
     * Determines if employee should appear with full unavailability treatment (60% opacity + 80% size).
    * Used for CSS class binding in employee card component.
    * 
    * Full unavailability applies to:
    * - Employees on break in any channel (most unavailable - cannot work anywhere)
    * - Employees busy instore when viewed in phone/online columns (cannot help other channels while busy instore)
    * 
    * @param emp Employee to check
    * @param channel Channel context for unavailability (instore, phone, online)
    * @returns true if should apply .unavailable class (60% opacity + 80% size)
    */
   shouldApplyFullUnavailability(emp: NormalizedEmployee, channel: ChannelType): boolean {
     // Employees on break show unavailable in all channels
     if (emp.globalState === 'break') {
       return true;
     }

      // In phone/online columns, busy instore employees show unavailable
      if (channel !== 'instore') {
        const instoreState = emp.channels.instore.state;
        const busyStates = ['with_guest', 'with_apt', 'with_guest_delivery', 'with_apt_delivery'];
        if (busyStates.includes(instoreState)) {
          return true;
        }

        // FR-P3-003, FR-P3-004, FR-P3-005: Cross-channel UP/On Deck unavailability
        // If employee is UP for instore, they are unavailable in phone/online
        // unless the board config explicitly allows it
        // These rules only apply when instore channel is BOTH purchased AND enabled
        const instorePurchased = this.boardConfig?.instoreChannelPurchased !== false;
        const instoreEnabled = this.boardConfig?.enableInstoreChannel !== false;
        if (instorePurchased && instoreEnabled) {
          const isUpForInstore = this.upInstore?.email === emp.email;
          const isOnDeckForInstore = this.onDeckInstore?.email === emp.email;

          if (channel === 'phone') {
            // Check if UP for instore should be unavailable for phone
            // Default: allowUpToTakePhone = false (UP is unavailable)
            if (isUpForInstore && !this.boardConfig?.allowUpToTakePhone) {
              return true;
            }
            // Check if On Deck for instore should be unavailable for phone
            // Default: allowOnDeckToTakePhone = true (On Deck is available)
            if (isOnDeckForInstore && this.boardConfig?.allowOnDeckToTakePhone === false) {
              return true;
            }
          }

          if (channel === 'online') {
            // Check if UP for instore should be unavailable for online
            // Default: allowUpToTakeInternet = false (UP is unavailable)
            if (isUpForInstore && !this.boardConfig?.allowUpToTakeInternet) {
              return true;
            }
            // Check if On Deck for instore should be unavailable for online
            // Default: allowOnDeckToTakeInternet = true (On Deck is available)
            if (isOnDeckForInstore && this.boardConfig?.allowOnDeckToTakeInternet === false) {
              return true;
            }
          }
        }
      }

     return false;
   }

   /**
    * Determines if employee should appear with size-only unavailability treatment (80% size, 100% opacity).
    * Used for CSS class binding in employee card component (Floor Ups column only).
    * 
    * Size-only unavailability applies to:
    * - Employees with appointment/delivery in Floor Ups column (can still take breaks/other channels)
    * 
    * @param emp Employee to check
    * @param channel Channel to check for unavailability context
    * @returns true if should apply .unavailable-size-only class (80% size only)
    */
   shouldApplySizeOnlyUnavailability(emp: NormalizedEmployee, channel: ChannelType): boolean {
     // Only applies to Floor Ups (instore) column
     if (channel !== 'instore') {
       return false;
     }

     // If on break, use full unavailability instead
     if (emp.globalState === 'break') {
       return false;
     }

     const instoreState = emp.channels.instore.state;
     const busyStates = ['with_apt', 'with_apt_delivery'];
     
     return busyStates.includes(instoreState);
   }
  
   /**
    * Converts a NormalizedEmployee back to legacy Employee format for UI compatibility.
    * This is a temporary bridge during migration.
    * Public to allow template access via property binding.
    * 
    * @param normalized Normalized employee to convert
    * @param channel Channel to extract state/order from (defaults to instore)
    */
   normalizedToEmployee(normalized: NormalizedEmployee, channel: ChannelType = 'instore'): Employee {
    const channelData = normalized.channels[channel];
    return {
      email: normalized.email,
      name: normalized.name,
      phone: normalized.phone,
      photoUrl: normalized.photoUrl,
      // Map 'available' back to 'up' for UI compatibility, or use globalState if on break
      state: normalized.globalState === 'break' ? 'break' : 
             channelData.state === 'available' ? 'up' : channelData.state,
      order: channelData.order,
    };
  }
  updateBoardState() {
    if (this.users?.length > 0) {
      this.active = getUsersNotInState(this.users, 'inactive');
      this.inactive = getUsersInState(this.users, 'inactive');
      this.setUpAndOnDeck(this.active);
    }
  }
  setUpAndOnDeck(active: Employee[]) {
    let available = [];
    for (let i = 0; i < active.length; i++) {
      let emp = active[i];
      if (emp.state == 'up') {
        available.push(emp);
      }
    }

    this.up = available[0];
    this.onDeck = available[1];
  }
}

export class Board {
  public viewerBackground: string | undefined;
  public logoUrl: string | undefined;
  public members: string[] | undefined;
  public deliveries: BoardDelivery[] | undefined;
  public config: { showDelivery?: boolean } | undefined;
}

export class Employee {
  public name: string | undefined;
  public phone: string | undefined;
  public state: string | undefined;
  public photoUrl: string | undefined;
  public email: string | undefined;
  public order: number = 0;
}

export interface Delivery {
  id: string;
  emp: string;
  vehicle: Vehicle;
  customer_in_store: boolean;
  stock_number: string;
  arrival_time: Date;
}

export interface BoardDelivery {
  id: string;
  emp: string;
  vehicle: Vehicle;
  customer_in_store: boolean;
  stock_number: string;
  arrival_time: string;
}

export interface Vehicle {
  color: string;
  model: string;
  year: number;
}
